import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, message } from 'antd';
import styled from 'styled-components';

import {
  useMySlackTokenStatusQuery,
  useSaveMySlackUserTokenMutation,
  MySlackTokenStatusDocument,
} from 'core/graphql';
import { Colors } from 'services/ColorService';

interface ISlackManagerTokenConfigProps {
  groupId: string;
}

/**
 * Per-manager Slack user token (xoxp) for THIS group's workspace. With the
 * token saved, DMs to students go out as the manager (not the bot) and mirror
 * back into the LMS without a webhook. The token is write-only — the status
 * query returns only hasToken + the resolved Slack user id.
 */
export const SlackManagerTokenConfig = memo(
  ({ groupId }: ISlackManagerTokenConfigProps) => {
    const { t } = useTranslation();
    const { data } = useMySlackTokenStatusQuery({
      variables: { groupId },
      fetchPolicy: 'cache-and-network',
    });
    const status = data?.mySlackUserToken;
    const configured = !!status?.hasToken;

    const [userToken, setUserToken] = useState('');
    const [editing, setEditing] = useState(false);

    const [save, { loading }] = useSaveMySlackUserTokenMutation({
      refetchQueries: [
        { query: MySlackTokenStatusDocument, variables: { groupId } },
      ],
      awaitRefetchQueries: true,
    });

    const onSave = useCallback(async () => {
      if (!userToken.trim()) return;
      const result = await save({
        variables: { groupId, userToken: userToken.trim() },
      });
      const payload = result.data?.setMySlackUserToken;
      if (payload?.success) {
        setUserToken('');
        setEditing(false);
        message.success(t('pages.slackConfig.managerTokenSuccess'));
        return;
      }
      const error = payload?.customErrors?.[0];
      if (error?.code === 'wrong_workspace') {
        message.error(t('pages.slackConfig.managerTokenWrongWorkspace'));
      } else if (error?.code === 'no_workspace') {
        message.error(t('pages.slackConfig.managerTokenNoWorkspace'));
      } else if (error?.code === 'slack_error') {
        message.error(t('pages.slackConfig.managerTokenSlackError'));
      } else if (error?.message) {
        message.error(error.message);
      }
    }, [save, groupId, userToken, t]);

    return (
      <Card>
        <CardTitle>{t('pages.slackConfig.managerTokenTitle')}</CardTitle>
        <Hint>{t('pages.slackConfig.managerTokenHint')}</Hint>
        <GuideCollapse ghost>
          <Collapse.Panel
            header={t('pages.slackConfig.managerTokenGuideTitle')}
            key="guide"
          >
            <GuideList>
              <li>
                {t('pages.slackConfig.managerTokenGuideStep1')}{' '}
                <GuideLink
                  href="https://api.slack.com/apps"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  api.slack.com/apps
                </GuideLink>
              </li>
              <li>{t('pages.slackConfig.managerTokenGuideStep2')}</li>
              <li>{t('pages.slackConfig.managerTokenGuideStep3')}</li>
              <li>{t('pages.slackConfig.managerTokenGuideStep4')}</li>
              <li>{t('pages.slackConfig.managerTokenGuideStep5')}</li>
              <li>{t('pages.slackConfig.managerTokenGuideStep6')}</li>
            </GuideList>
          </Collapse.Panel>
        </GuideCollapse>
        {configured && !editing ? (
          <>
            <Linked>
              ✓{' '}
              {t('pages.slackConfig.managerTokenSaved', {
                id: status?.slackUserId || '—',
              })}
            </Linked>
            <ChangeBtn type="button" onClick={() => setEditing(true)}>
              {t('pages.slackConfig.change')}
            </ChangeBtn>
          </>
        ) : (
          <>
            <Row>
              <Input
                value={userToken}
                type="password"
                placeholder={t('pages.slackConfig.managerTokenPlaceholder')}
                onChange={(e) => setUserToken(e.target.value)}
              />
              <SaveButton
                type="button"
                disabled={loading || !userToken.trim()}
                onClick={onSave}
              >
                {t('pages.slackConfig.save')}
              </SaveButton>
              {configured ? (
                <CancelBtn
                  type="button"
                  onClick={() => {
                    setUserToken('');
                    setEditing(false);
                  }}
                >
                  {t('pages.slackConfig.cancel')}
                </CancelBtn>
              ) : null}
            </Row>
            {!configured ? (
              <NotLinked>
                {t('pages.slackConfig.managerTokenMissing')}
              </NotLinked>
            ) : null}
          </>
        )}
      </Card>
    );
  },
);

SlackManagerTokenConfig.displayName = 'SlackManagerTokenConfig';

const Card = styled.div`
  margin-top: 16px;
  padding: 14px 16px;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 10px;
  background: ${Colors.White};
`;

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${Colors.Gray};
  margin-bottom: 6px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${Colors.Gray};
  margin-bottom: 10px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const Input = styled.input`
  flex: 1;
  min-width: 140px;
  box-sizing: border-box;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
`;

const SaveButton = styled.button`
  border: 1px solid ${Colors.Blue};
  background: ${Colors.Blue};
  color: ${Colors.White};
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const Linked = styled.div`
  font-size: 12px;
  color: ${Colors.SuccessDefault};
  margin-top: 8px;
`;

const NotLinked = styled.div`
  font-size: 12px;
  color: ${Colors.Gray};
  margin-top: 8px;
`;

const GuideCollapse = styled(Collapse)`
  margin-bottom: 8px;

  .ant-collapse-header {
    padding: 4px 0 !important;
    font-size: 12px;
    color: ${Colors.Blue};
  }

  .ant-collapse-content-box {
    padding: 4px 0 8px !important;
  }
`;

const GuideList = styled.ol`
  font-size: 12px;
  color: ${Colors.Black};
  margin: 0;
  padding-left: 18px;

  li + li {
    margin-top: 4px;
  }
`;

const GuideLink = styled.a`
  color: ${Colors.Blue};
`;

const ChangeBtn = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin-top: 4px;
  font: inherit;
  font-size: 12px;
  color: ${Colors.Blue};
  cursor: pointer;
`;

const CancelBtn = styled.button`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 12px;
  color: ${Colors.Gray};
  cursor: pointer;
`;
